import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { manpowerApi, type MpRequest, type MpRequestStatus, type MpRequestType } from '@/lib/apiClient';
import { fmtDate, fmtDateTime } from '@/lib/dates';
import TriageRequestModal from './TriageRequestModal';
import { STATUS_LABELS } from './lib/requestLifecycle';

/**
 * Manpower requests — the scheduler inbox (#303 / #420, slice 1 of 6).
 *
 * Somebody asks for a job's crew to change; a scheduler reads it and books the
 * work by hand. Until 2026-08-31 the asking half lived in Formstack form
 * 6095863, which opened blank and could not pre-fill anything. The product
 * owner and operations decided to build the form natively here and retire it.
 *
 * Slice 2 added the in-app form (a job's Manpower tab); slice 3 added triage —
 * the Triage… button on each row, for `manpower.edit` holders only, opens
 * TriageRequestModal. The Decision column is what came out of it: the status,
 * who decided and when, and the scheduler's note to the PM.
 *
 * ⚠️ NOTHING HERE EVER WRITES THE CREW GRID. Settled in #420 and restated by
 * operations: "All requests should be entered by the Scheduler to ensure all context
 * is captured that was discussed during the approval process." This view shows
 * the ask; the grid stays a human decision.
 */

// ─── Shared style constants (matched to HistoryView / LeadersView) ───────────

const TH: React.CSSProperties = {
  background: 'var(--ew-dark)',
  color: 'var(--ew-white)',
  fontWeight: 600,
  fontSize: 11,
  padding: '8px 10px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  borderRight: '1px solid rgba(255,255,255,0.07)',
};

const TD: React.CSSProperties = {
  borderBottom: '1px solid var(--ew-n-100)',
  borderRight: '1px solid var(--ew-n-100)',
  padding: '7px 10px',
  fontSize: 12,
  verticalAlign: 'top',
};

const SELECT: React.CSSProperties = {
  fontFamily: 'var(--ew-font)',
  fontSize: 12,
  padding: '5px 8px',
  border: '1px solid var(--ew-n-200)',
  borderRadius: 'var(--ew-r-md)',
  background: 'var(--ew-white)',
  color: 'var(--ew-n-600)',
  maxWidth: 260,
};

const PAGE_SIZE = 50;

/** What each request type means in plain words. An unmapped value falls back to
 *  the raw slug rather than rendering blank, so a type added server-side
 *  degrades to something readable instead of vanishing. */
const TYPE_LABELS: Record<string, string> = {
  'pm-change':       'PM request',
  'systac-forecast': 'SYSTAC forecast',
  'pco':             'PCO work',
  'co':              'CO work',
};

/** Status filter chips. `undefined` value = every status. 'open' is the pair
 *  that still needs a scheduler, which is why it leads. */
const STATUS_TABS = [
  { key: 'open',     label: 'Needs action' },
  { key: 'booked',   label: 'Booked' },
  { key: 'declined', label: 'Declined' },
  { key: 'archived', label: 'Archived' },
  { key: 'all',      label: 'All' },
] as const;

type StatusTab = (typeof STATUS_TABS)[number]['key'];

/**
 * An imported Formstack row from before ~11 Dec 2025, when the form gained its
 * crew and date fields: the ask lives in the reason and nowhere else (#303
 * slice 6). 194 of 297 real submissions are this shape. Formstack rows only —
 * an in-app request cannot be blank here, and if one ever were, "—" is right.
 */
export function isReasonOnly(r: Pick<MpRequest, 'source' | 'crewSize' | 'startDate' | 'endDate'>): boolean {
  return r.source === 'formstack' && r.crewSize == null && r.startDate == null && r.endDate == null;
}

/** "10904 Example Site", or whichever half exists. (Invented title:
 *  comments here are published — see vite.config.ts.) */
export function jobLabel(jobNumber: string | null, title: string | null): string {
  if (jobNumber && title) return `${jobNumber} ${title}`;
  return jobNumber ?? title ?? '—';
}

/**
 * What an empty result means. Three different things, and they are not
 * interchangeable — exported so each branch is testable, since two of them
 * depend on component state a static render cannot reach.
 *
 * The middle one says where requests come from, so a scheduler who opens a
 * blank inbox knows the door exists and where it is.
 */
export function emptyMessage(opts: { filtered: boolean; anyAtAll: boolean }): string {
  if (opts.filtered) return 'No manpower requests match these filters.';
  if (!opts.anyAtAll) {
    return 'No manpower requests yet. PMs raise them from a job’s Manpower tab, and they land here.';
  }
  return 'No manpower requests with this status.';
}

/**
 * Whether a previous page may stay on screen while the next one loads.
 *
 * ⚠️ Only when the FILTERS are unchanged. React Query's plain
 * `keepPreviousData` hands the old result to the new query key and reports it
 * as success, so changing the status tab renders the previous tab's rows under
 * the new heading until the fetch lands — briefly showing declined requests as
 * though they needed action. Same defect the History view hit in review (#421).
 */
export function keepAcrossPages<T>(
  prev: T | undefined,
  prevFilters: RequestFilters | undefined,
  now: RequestFilters,
): T | undefined {
  if (!prevFilters) return undefined;
  const same = prevFilters.tab === now.tab && prevFilters.requestType === now.requestType;
  return same ? prev : undefined;
}

export type RequestFilters = { tab: StatusTab; requestType: MpRequestType | '' };

/** The tab's filter translated into API arguments. 'all' sends neither. */
export function tabParams(tab: StatusTab): { status?: MpRequestStatus; open?: boolean } {
  if (tab === 'open') return { open: true };
  if (tab === 'all') return {};
  return { status: tab as MpRequestStatus };
}

/** The count shown on a tab, from the API's whole-table status counts. */
export function tabCount(
  tab: StatusTab,
  counts: Record<MpRequestStatus, number> | undefined,
  openCount: number | undefined,
): number | null {
  if (!counts) return null;
  if (tab === 'open') return openCount ?? 0;
  if (tab === 'all') return Object.values(counts).reduce((a, b) => a + b, 0);
  return counts[tab as MpRequestStatus] ?? 0;
}

/**
 * Whether the pager renders. Always on a later page, even with no rows: a
 * decision refetches the current page, and if the transitioned row was the last
 * one on page 2+ the filter no longer matches anything there. Hiding the pager
 * with the table would strand the scheduler on an empty page with no "Newer"
 * (Codex, #440).
 */
export function pagerVisible(rowCount: number, page: number): boolean {
  return rowCount > 0 || page > 0;
}

/**
 * @param canTriage whether this user may decide requests — `manpower.edit`,
 *   resolved by ManpowerPage from the same `canEdit && !fabEditOnly` that gates
 *   the crew grid. The API enforces it again; this only shows or hides the
 *   button so a read-only viewer is not offered a control that 403s.
 */
export default function RequestsView({ canTriage = false }: { canTriage?: boolean }) {
  const [tab, setTab] = useState<StatusTab>('open');
  // The request whose triage modal is open. ⚠️ Mounted per open (see the JSX at
  // the bottom) — the modal is created for this request and destroyed on close.
  const [triage, setTriage] = useState<MpRequest | null>(null);
  const [requestType, setRequestType] = useState<MpRequestType | ''>('');
  // One keyset cursor per page already visited — the stack is what makes
  // "Newer" work without an offset. An empty stack is page 0.
  const [cursors, setCursors] = useState<{ at: string; id: number }[]>([]);
  const page = cursors.length;
  const cursor = cursors[cursors.length - 1];

  // Any filter change invalidates every cursor — they point into the old result
  // set. Without this, switching tabs while on page 2 shows an empty table and
  // reads as "there are none of these".
  useEffect(() => { setCursors([]); }, [tab, requestType]);

  const q = useQuery({
    queryKey: ['manpower', 'requests', { tab, requestType, page, cursor }],
    queryFn: () => manpowerApi.requests({
      ...tabParams(tab),
      requestType: requestType || undefined,
      cursorAt: cursor?.at,
      cursorId: cursor?.id,
      limit: PAGE_SIZE,
    }),
    placeholderData: (prev, prevQuery) =>
      keepAcrossPages(prev, prevQuery?.queryKey?.[2] as RequestFilters | undefined,
        { tab, requestType }),
  });

  const rows = q.data?.rows ?? [];
  const total = Object.values(q.data?.statusCounts ?? {}).reduce((a, b) => a + b, 0);

  return (
    <div>
      {/* ── Status tabs ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {STATUS_TABS.map((t) => {
          const active = tab === t.key;
          const n = tabCount(t.key, q.data?.statusCounts, q.data?.openCount);
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              aria-pressed={active}
              style={{
                padding: '5px 12px',
                fontSize: 12,
                fontWeight: 700,
                fontFamily: 'var(--ew-font)',
                border: `1px solid ${active ? 'var(--ew-green)' : 'var(--ew-n-200)'}`,
                borderRadius: 'var(--ew-r-pill)',
                cursor: 'pointer',
                background: active ? 'var(--ew-green)' : 'var(--ew-white)',
                color: active ? 'var(--ew-white)' : 'var(--ew-n-500)',
              }}
            >
              {t.label}
              {n !== null && (
                <span style={{ marginLeft: 6, opacity: 0.75, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
              )}
            </button>
          );
        })}

        <select
          aria-label="Request type"
          style={{ ...SELECT, marginLeft: 4 }}
          value={requestType}
          onChange={(e) => setRequestType(e.target.value as MpRequestType | '')}
        >
          <option value="">All types</option>
          {Object.entries(TYPE_LABELS).map(([k, label]) => (
            <option key={k} value={k}>{label}</option>
          ))}
        </select>

        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ew-n-400)' }}>
          A scheduler books approved crew by hand — a request never writes the grid.
        </span>
      </div>

      {/* ── States ── */}
      {q.isPending && (
        <div style={{ padding: 24, fontSize: 13, color: 'var(--ew-n-400)' }}>Loading requests…</div>
      )}

      {q.isError && (
        <div style={{ padding: 16, fontSize: 13, color: 'var(--ew-danger, #b8321c)' }}>
          Couldn’t load the manpower requests.{' '}
          <button
            type="button"
            className="btn btn-secondary"
            style={{ fontSize: 12, padding: '4px 10px', marginLeft: 8 }}
            onClick={() => q.refetch()}
          >
            Retry
          </button>
        </div>
      )}

      {q.isSuccess && rows.length === 0 && (
        <div style={{ padding: 24, fontSize: 13, color: 'var(--ew-n-400)', maxWidth: '68ch', lineHeight: 1.55 }}>
          {page > 0
            ? 'Nothing left on this page — the last request here was just decided. Go back a page.'
            : emptyMessage({ filtered: requestType !== '', anyAtAll: total > 0 })}
        </div>
      )}

      {q.isSuccess && pagerVisible(rows.length, page) && (
        <>
          {rows.length > 0 && (
          <div style={{ overflowX: 'auto', border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-md)' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
              <thead>
                <tr>
                  <th style={{ ...TH, width: 150 }}>Raised</th>
                  <th style={{ ...TH, width: 130 }}>Type</th>
                  <th style={{ ...TH, width: 240 }}>Job</th>
                  <th style={{ ...TH, width: 150 }}>Crew</th>
                  <th style={{ ...TH, width: 170 }}>Dates</th>
                  <th style={{ ...TH, width: 150 }}>Raised by</th>
                  <th style={TH}>Reason</th>
                  <th style={{ ...TH, width: 220, ...(canTriage ? {} : { borderRight: 'none' }) }}>Decision</th>
                  {canTriage && <th style={{ ...TH, width: 84, borderRight: 'none' }} aria-label="Actions" />}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td style={{ ...TD, whiteSpace: 'nowrap', color: 'var(--ew-n-500)' }}>
                      {fmtDateTime(r.createdAt)}
                    </td>
                    <td style={TD}>
                      {TYPE_LABELS[r.requestType] ?? r.requestType}
                      {/* Imported history could not record whether it was an
                          initial ask or a change — the old form never asked. */}
                      {r.stage && (
                        <div style={{ fontSize: 10, color: 'var(--ew-n-400)', marginTop: 2 }}>{r.stage}</div>
                      )}
                      {/* The change the crew is for (pco / co). Number only — the
                          inbox is visible to everyone who reaches it, never the amount. */}
                      {r.changeNo && (
                        <div style={{ fontSize: 10, color: 'var(--ew-n-500)', marginTop: 2, fontWeight: 600 }}>{r.changeNo}</div>
                      )}
                    </td>
                    <td style={TD}>
                      {r.projectId
                        ? <a href={`/projects/${r.projectId}`} style={{ color: 'var(--ew-green)' }}>
                            {jobLabel(r.pmtJobNumber ?? r.jobNumber, r.projectTitle)}
                          </a>
                        : <span style={{ color: 'var(--ew-n-400)' }}>{jobLabel(r.jobNumber, r.jobName)}</span>}
                      {/* ⚠️ Show the submitted job number when it differs from
                          the matched job's. One real submission carried 10854
                          for four months while meaning 10923 — hiding the
                          discrepancy is how that goes unnoticed. */}
                      {r.pmtJobNumber && r.jobNumber && r.pmtJobNumber !== r.jobNumber && (
                        <div style={{ fontSize: 10, color: 'var(--ew-performance)', marginTop: 2 }}>
                          submitted as {r.jobNumber}
                        </div>
                      )}
                    </td>
                    {/* FREE TEXT, rendered exactly as given. '1-2' and 'Whole
                        crew' are real values; parsing it would lose the ask. */}
                    <td style={TD}>
                      {r.crewSize ?? (
                        isReasonOnly(r)
                          // Imported history from before the form gained its
                          // structured fields (~11 Dec 2025): the ask is in the
                          // reason, and only there. Say so rather than "—".
                          ? <span style={{ fontSize: 10, color: 'var(--ew-n-400)', fontStyle: 'italic' }}>reason only</span>
                          : <span style={{ color: 'var(--ew-n-400)' }}>—</span>
                      )}
                      {r.phase && (
                        <div style={{ fontSize: 10, color: 'var(--ew-n-400)', marginTop: 2 }}>{r.phase}</div>
                      )}
                    </td>
                    <td style={{ ...TD, whiteSpace: 'nowrap' }}>
                      {r.startDate || r.endDate
                        ? <>{fmtDate(r.startDate)} → {fmtDate(r.endDate)}</>
                        : <span style={{ color: 'var(--ew-n-400)' }}>—</span>}
                    </td>
                    <td style={TD}>
                      {r.submittedBy ?? <span style={{ color: 'var(--ew-n-400)' }}>Unknown</span>}
                      {/* The PM is not the submitter in about half of all real
                          submissions, so both are worth showing. */}
                      {r.pm && r.pm !== r.submittedBy && (
                        <div style={{ fontSize: 10, color: 'var(--ew-n-400)', marginTop: 2 }}>PM: {r.pm}</div>
                      )}
                    </td>
                    {/* Never summarised. A request can span two jobs with only
                        one in a structured field, and the reason is where that
                        shows up — it has to reach the scheduler intact. */}
                    <td style={{ ...TD, whiteSpace: 'pre-wrap' }}>
                      {r.reason ?? <span style={{ color: 'var(--ew-n-400)' }}>—</span>}
                    </td>
                    {/* The scheduler's half of the conversation. The note is the
                        PM's only view of what was agreed, so it is shown whole. */}
                    <td style={{ ...TD, ...(canTriage ? {} : { borderRight: 'none' }) }}>
                      <span style={{ fontWeight: 600 }}>{STATUS_LABELS[r.status] ?? r.status}</span>
                      {r.decidedBy && (
                        <div style={{ fontSize: 10, color: 'var(--ew-n-400)', marginTop: 2 }}>
                          {r.decidedBy}{r.decidedAt ? ` · ${fmtDateTime(r.decidedAt)}` : ''}
                        </div>
                      )}
                      {r.schedulerNote && (
                        <div style={{ marginTop: 4, whiteSpace: 'pre-wrap', color: 'var(--ew-n-600)' }}>{r.schedulerNote}</div>
                      )}
                    </td>
                    {/* Archived rows are imported history — nothing to decide, so
                        no button rather than a disabled one that invites a click. */}
                    {canTriage && (
                      <td style={{ ...TD, borderRight: 'none', whiteSpace: 'nowrap' }}>
                        {r.status !== 'archived' && (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ fontSize: 12, padding: '4px 10px' }}
                            onClick={() => setTriage(r)}
                          >
                            Triage…
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          {/* Keyset pager, same contract as the History view: Older pushes this
              page's last row as the next cursor, Newer pops. Not an accumulating
              "load more" — filters change the result set underneath, and an
              accumulated list would keep rows that no longer match. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 12, padding: '5px 12px' }}
              disabled={page === 0 || q.isFetching}
              onClick={() => setCursors((c) => c.slice(0, -1))}
            >
              ← Newer
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: 12, padding: '5px 12px' }}
              disabled={!q.data?.nextCursor || q.isFetching}
              onClick={() => {
                const next = q.data?.nextCursor;
                if (next) setCursors((c) => [...c, next]);
              }}
            >
              Older →
            </button>
            <span style={{ fontSize: 11, color: 'var(--ew-n-400)' }}>Page {page + 1}</span>
          </div>
        </>
      )}

      {/* ⚠️ Mounted only while open, keyed by request. Never keep it mounted and
          reset it on reopen — #439 shipped two bugs that way. */}
      {triage && (
        <TriageRequestModal key={triage.id} request={triage} onClose={() => setTriage(null)} />
      )}
    </div>
  );
}
